import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

import { OfferStats } from '../../../models/offerStats';
import { SearchRecord } from '../../../models/searchRecord';

import { ConfigService } from '../../config.service';
import { DataStorageService } from '../../data-storage.service';
import { SecurityService } from '../../security.service';
import { Offer } from '../../../models/offer';
import { OfferOptionsModel } from '../../../models/offerOptions.model';

interface ViewConfig {
  vatTenThousandth: number;
}

@Injectable()
export class ApiOfferService {

  constructor(public dataStorageService: DataStorageService,
    public securityService: SecurityService,
    public configService: ConfigService,
    public http: HttpClient
  ) {
  }

  getOfferOverview(userID?: number | number[] | string, period?: { dateFrom?: string, dateTo?: string }): Promise<OfferStats> {
    return new Promise<OfferStats>((resolve, reject) => {
      let userUrlAddon = '';
      if (userID) {
        let effect;
        if (Array.isArray(userID)) {
          effect = '';
          userID.forEach(value => {
            effect += value;
            effect += ',';
          });
          effect = effect.substr(0, effect.length - 1);
        } else {
          effect = userID;
        }
        userUrlAddon = '?creators-ids=' + effect;
      }
      let periodUrlAddon = '';
      if (period) {
        if (userUrlAddon) {
          periodUrlAddon += '&';
        } else {
          periodUrlAddon += '?';
        }
        if (period.dateFrom) {
          periodUrlAddon += 'date-from=' + period.dateFrom;
        } else {
          periodUrlAddon += 'date-from=' + '1990-06-27';
        }
        periodUrlAddon += '&';
        if (period.dateTo) {
          periodUrlAddon += 'date-to=' + period.dateTo;
        } else {
          periodUrlAddon += 'date-to=' + '2666-06-27';
        }
      }
      const url = this.configService.host
        + '/api/account/current/offer-stats'
        + userUrlAddon
        + periodUrlAddon;
      this.http.get<OfferStats>(url)
        .toPromise()
        .then((response) => {
          resolve(response);
        })
        .catch((error: HttpErrorResponse) => {
          if (error.status === 401) {
            this.securityService.refreshToken()
              .then(() => {
                this.getOfferOverview(userID, period)
                  .then(data => resolve(data));
              });
          } else {
            alert(error.message);
            reject(error);
          }
        });
    });
  }

  getOffers(status?: string,
    offerNumber?: string,
    companyName?: string,
    contactPerson?: string,
    salesStage?: string,
    nextStep?: string,
    postalCode?: string,
    city?: string,
    phoneNumber?: string,
    mobileNumber?: string,
    dateFrom?: string,
    dateTo?: string,
    creatorsIDs?: string,
    pageNumber?: number,
    pageSize?: number): Promise<SearchRecord[]> {

    let url = this.configService.host
      + '/api/offer';
    url += status
      || offerNumber
      || companyName
      || contactPerson
      || salesStage
      || nextStep
      || postalCode
      || city
      || phoneNumber
      || mobileNumber
      || dateFrom
      || dateTo
      || creatorsIDs
      || pageNumber
      || pageSize ? '?' : '';
    url += status ? 'status=' + status + '&' : '';
    url += offerNumber ? 'number=' + offerNumber + '&' : '';
    url += companyName ? 'company-name=' + companyName + '&' : '';
    url += contactPerson ? 'contact-person=' + contactPerson + '&' : '';
    url += salesStage ? 'sales-stage=' + salesStage + '&' : '';
    url += nextStep ? 'next-step=' + nextStep + '&' : '';
    url += postalCode ? 'postal-code=' + postalCode + '&' : '';
    url += city ? 'city=' + city + '&' : '';
    url += phoneNumber ? 'phone-nr=' + phoneNumber + '&' : '';
    url += mobileNumber ? 'mobile-nr=' + mobileNumber + '&' : '';
    url += dateFrom ? 'date-from=' + dateFrom + '&' : '';
    url += dateTo ? 'date-to=' + dateTo + '&' : '';
    url += creatorsIDs ? 'creators-ids=' + creatorsIDs + '&' : '';
    url += pageNumber ? 'page-nr=' + pageNumber + '&' : '';
    url += pageSize ? 'page-size=' + pageSize + '&' : '';
    url = url[url.length - 1] === '&' ? url.slice(0, url.length - 1) : url;
    return new Promise<SearchRecord[]>((resolve, reject) => {
      this.http.get(url)
        .toPromise()
        .then((response) => {
          resolve(response['records'] as SearchRecord[]);
        }).catch((error: HttpErrorResponse) => {
          if (error.status === 401) {
            this.securityService.refreshToken().then(() => {
              this.getOffers()
                .then(data => resolve(data));
            });
          } else {
            alert(error.message);
            reject(error);
          }
        });
    });
  }

  getOfferOptions(offerID: number): Promise<any> {
    const url = this.configService.host
      + '/api/offer/'
      + offerID;

    return new Promise<any>((resolve, reject) => {
      this.http.get<OfferOptionsModel>(url)
        .toPromise()
        .then((response) => {
          const divider = 10000;
          const currentState = this.dataStorageService.currentState;
          currentState.discount = response.devicesDiscountPercent;
          currentState.wishedSolution.FlatRateHOSTED = response.options.bizFlatRatesHosted;
          currentState.wishedSolution.FlatRateSIPTRUNK = response.options.bizFlatRatesSipTrunk;
          currentState.wishedSolution.NewPhoneNumbers = response.options.newNumbers;
          currentState.wishedSolution.PeoplephoneSIPTRUNK = response.options.peoplefoneSIPTRUNK;
          currentState.wishedSolution.PeoplephoneHosted = response.options.peoplefoneHosted;
          currentState.wishedSolution.ThreeCX = response.options.threeCX;
          currentState.wishedSolution.MicrosoftTeams = response.options.msTeams;
          currentState.wishedSolution.PeoplefoneBundle = response.options.pfBundle;
          currentState.wishedSolution.PhoneNumbersToBePorted = response.options.portedNumbers;
          currentState.wishedSolution.PeoplephoneInternetCyberlink = response.options.internetCyberlink;
          currentState.wishedSolution.PeoplephoneInternetIPworldcom = response.options.internetIpworldcom;
          currentState.wishedSolution.PeoplephoneDevicesDataBase = response.options.pfDevices;
          currentState.wishedSolution.MyDevicesDataBase = response.options.myDevices;
          currentState.wishedSolution.PeoplefoneMittel = response.options.pfMittel;
          currentState.wishedSolution.Datasim = response.options.datasim;
          currentState.wishedSolution.OperatorConnect = response.options.operatorConnect;
          currentState.wishedSolution.SBCplus = response.options.sbcplus;
          currentState.wishedSolution.mobile = response.options.mobile;
          currentState.status = response.status;
          this.dataStorageService.vatValue = parseInt(response.vatTenThousandth.toString(), 10) / divider;
          resolve();
        }).catch((error: HttpErrorResponse) => {
          if (error.status === 401) {
            this.securityService.refreshToken().then(() => {
              this.getOfferOptions(offerID)
                .then(() => resolve());
            });
          } else {
            alert(error.message);
            reject(error);
          }
        });
    });
  }

  postOffer(): Promise<any> {
    const url = this.configService.host
      + '/api/offer';
    const body = null;
    if (this.dataStorageService.currentState.status === 'OPEN') {
      return new Promise<any>((resolve, reject) => {
        this.http.post(url, body)
          .toPromise()
          .then((response) => {
            this.dataStorageService.currentState.offerID = response['id'];
            this.dataStorageService.currentState.offerNumber = response['number'];
            this.dataStorageService.vatValue = response['vatTenThousandth'] / 10000;
            resolve();
          }).catch((error: HttpErrorResponse) => {
            if (error.status === 401) {
              this.securityService.refreshToken().then(() => {
                this.postOffer()
                  .then(() => {
                    resolve();
                  });
              });
            } else {
              if (error.status >= 400 && error.status < 500) {
                alert('ERROR: Please contact with your administrator');
              } else {
                alert('Please check your internet connection');
              }
              reject();
            }
          });
      });
    } else {
      return new Promise<any>((resolve, reject) => {
        alert('You can not modify WON/LOST offer');
        reject();
      });
    }
  }

  postOfferDuplicate(): Promise<Offer> {
    const url = this.configService.host
      + '/api/offer';
    const body = null;
    if (this.dataStorageService.currentState.status === 'OPEN') {
      return new Promise<Offer>((resolve, reject) => {
        this.http.post(url, body)
          .subscribe((response) => {
            const dummy = new Offer();
            dummy.offerID = response['id'];
            dummy.offerNumber = response['number'];
            resolve(dummy);
          }, (error: HttpErrorResponse) => {
            if (error.status === 401) {
              this.securityService.refreshToken().then(() => {
                this.postOffer()
                  .then(data => {
                    resolve(data);
                  });
              });
            } else {
              if (error.status >= 400 && error.status < 500) {
                alert('ERROR: Please contact with your administrator');
              } else {
                alert('Please check your internet connection');
              }
              reject();
            }
          });
      });
    } else {
      return new Promise<any>((resolve, reject) => {
        alert('You can not modify WON/LOST offer');
        reject();
      });
    }
  }

  putOfferOptions(offerToUpdate?: Offer): Promise<any> {
    const offerID = offerToUpdate ? offerToUpdate.offerID : this.dataStorageService.currentState.offerID;

    const url = this.configService.host
      + '/api/offer/'
      + offerID;
    const body = offerToUpdate ? offerToUpdate.generateUpdateObject() : this.dataStorageService.currentState.generateUpdateObject();
    if (this.dataStorageService.currentState.status === 'OPEN') {
      return new Promise<any>((resolve, reject) => {
        this.http.put(url, body)
          .toPromise()
          .then((response) => resolve(response)).catch((error: HttpErrorResponse) => {
            if (error.status === 401) {
              this.securityService.refreshToken().then(() => {
                this.putOfferOptions()
                  .then((data) => resolve(data));
              });
            } else {
              if (error.status >= 400 && error.status < 500) {
                alert('ERROR: Please contact with your administrator');
              } else {
                alert('Please check your internet connection');
              }
              reject();
            }
          });
      });
    } else {
      return new Promise<any>((resolve, reject) => {
        alert('You can not modify WON/LOST offer');
        reject();
      });
    }
  }

  deleteOffer(offerID: number): Promise<any> {
    const url = this.configService.host
      + '/api/offer/'
      + offerID;

    return new Promise<any>((resolve, reject) => {
      this.http.delete(url)
        .toPromise()
        .then(() => resolve())
        .catch((error: HttpErrorResponse) => {
          if (error.status === 401) {
            this.securityService.refreshToken()
              .then(() => this.deleteOffer(offerID).then(() => resolve()));
          } else {
            alert(error.message);
            reject(error);
          }
        });
    });
  }

  getPossibleOfferStatus(): Promise<string[]> { // mock

    const status = [
      'ALL',
      'OPEN',
      'WON',
      'LOST'
    ];

    return new Promise<string[]>((resolve, reject) => {
      resolve(status);
    });
  }

  getVATvalue(): Promise<number> {
    const url = this.configService.host
      + '/api/account/current/view-config';

    return new Promise<number>((resolve, reject) => {
      this.http.get<ViewConfig>(url).subscribe(response => {
        const divider = 10000;
        resolve(response.vatTenThousandth / divider);
      }, error => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          this.securityService.refreshToken()
            .then(() => {
              this.getVATvalue().then(vat => resolve(vat));
            });
        } else {
          alert(error.message);
          reject(error);
        }
      });

    });
  }
}



// WEBPACK FOOTER //
// ./src/app/apiServices/offer/api-offer.service.ts